import {
  ApolloClient, InMemoryCache, createHttpLink, ApolloLink, Observable, split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Platform } from 'react-native';
import * as Keychain from 'react-native-keychain';
import Constants from 'expo-constants';
import { RefreshTokenMutation } from '../gql/user/mutation';
import { notifyTokenRefresh } from './tokenRefreshBridge';

const apiUrl = Constants.expoConfig?.extra?.EXPO_PUBLIC_API_URL ?? '';

const httpLink = createHttpLink({
  uri: `${apiUrl}/api/graphql`,
});

async function getAccessToken() {
  if (Platform.OS === 'web') {
    return localStorage.getItem('accessToken');
  }
  try {
    const credentials = await Keychain.getGenericPassword();
    if (credentials) {
      return JSON.parse(credentials.password).accessToken;
    }
  } catch {}
  return null;
}

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${apiUrl.replace(/^http/, 'ws')}/api/graphql`,
    keepAlive: 10_000,
    connectionParams: async () => {
      const accessToken = await getAccessToken();
      return { authorization: accessToken ? `Bearer ${accessToken}` : '' };
    },
  }),
);

const authLink = setContext(async (_, { headers }) => {
  let accessToken;
  let refreshToken;

  if (Platform.OS === 'web') {
    accessToken = localStorage.getItem('accessToken');
    refreshToken = localStorage.getItem('refreshToken');
  } else {
    try {
      const credentials = await Keychain.getGenericPassword();
      if (credentials) {
        const tokenData = JSON.parse(credentials.password);
        accessToken = tokenData.accessToken;
        refreshToken = tokenData.refreshToken;
      }
    } catch (error) {
      console.log("Keychain couldn't be accessed!", error);
    }
  }

  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
    refreshToken,
  };
});

const errorLink = onError(({
  graphQLErrors, networkError, operation, forward,
}) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.message === 'Not Authorised!') {
        // error code is a string and not a number
        return new Observable((observer) => {
          (async () => {
            let oldRefreshToken;
            if (Platform.OS === 'web') {
              oldRefreshToken = localStorage.getItem('refreshToken');
            } else {
              try {
                const credentials = await Keychain.getGenericPassword();
                if (credentials) {
                  const tokenData = JSON.parse(credentials.password);
                  oldRefreshToken = tokenData.refreshToken;
                }
              } catch (keychainError) {
                console.log("Keychain couldn't be accessed!", keychainError);
              }
            }

            if (!oldRefreshToken) {
              if (Platform.OS === 'web') {
                localStorage.clear();
              } else {
                await Keychain.resetGenericPassword();
              }
              observer.error(networkError);
              return;
            }

            try {
              const { data } = await client.mutate({
                mutation: RefreshTokenMutation,
                variables: { refreshToken: oldRefreshToken },
              });

              const newAccessToken = data.refreshToken.accessToken;
              const newRefreshToken = data.refreshToken.refreshToken;

              if (Platform.OS === 'web') {
                localStorage.setItem('accessToken', newAccessToken);
                localStorage.setItem('refreshToken', newRefreshToken);
              } else {
                await Keychain.setGenericPassword('login', JSON.stringify({
                  accessToken: newAccessToken,
                  refreshToken: newRefreshToken,
                  email: data.refreshToken.user.email,
                  role: data.refreshToken.user.role,
                  username: `${data.refreshToken.user.firstName} ${data.refreshToken.user.lastName}`,
                }));
              }

              operation.setContext(({ headers }) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${newAccessToken}`,
                },
              }));

              // Sync new tokens back into AuthContext state
              notifyTokenRefresh({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
                user: data.refreshToken.user,
              });

              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            } catch (refreshError) {
              if (Platform.OS === 'web') {
                localStorage.clear();
              } else {
                await Keychain.resetGenericPassword();
              }
              observer.error(refreshError);
            }
          })();
        });
      }
    }
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    // TODO: Handle network errors, e.g., show a message to the user
  }
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  ApolloLink.from([authLink, errorLink, httpLink]),
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

export default client;
